import { styled } from "@mui/material/styles";
import { AcquireBookDataGrid } from "@/components/DataGrid";
import {
  type GridColDef,
  GridToolbarContainer,
  type GridValueFormatterParams,
} from "@mui/x-data-grid-pro";
import { Button as MuiButton, Stack, Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import { format } from "date-fns";
import { type AnalyzedRealEstate } from "@/features/ownerInfo/types";
import { useApiClient } from "@/hooks/useApiClient";
import { toast } from "react-toastify";
import { getOwnerAnalyzeFileApi } from "@/features/ownerInfo/api";
import OutputMenu from "@/features/ownerInfo/components/OutputMenu";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import { useShowSendToSamboKun } from "@/features/partner/hooks/useShowSendToSamboKun";
import { useSamboKunLimit } from "@/features/partner/hooks/useSamboKunLimit";
import {
  getSamboKunSendDisableReason,
  samboKunSendDisabledTooltip,
} from "@/features/partner/utils/isSendDisabled";
import { SendToSamboKunDialog } from "@/features/partner/components/SendToSamboKunDialog";
import { createSamboKunColumn } from "@/features/partner/components/samboKunColumn";

interface IProps {
  row: AnalyzedRealEstate[] | undefined;
  requestId: number | undefined;
  disabledAction: boolean;
  taskId?: number;
  identificationName?: string;
  isPropertyDetailOptionCredit?: boolean;
  isDmCredit?: boolean;
  // 不動産登記取得（PictureRequest）に紐づく解析かどうか。
  // false の場合は参謀くん連携 API がエラーになるため、連携ボタンを無効化する。
  hasTriggerPictureRequest?: boolean;
  /** 解析リクエストに紐づく物件数（連携ボタンの上限超過判定に使用）。 */
  succeedCount?: number;
  onDownloadComplete?: () => void;
}

const StyledDataGrid = styled(AcquireBookDataGrid)`
  // テーブルのセルをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-cell:focus-within,
  & .MuiDataGrid-cell:focus {
    outline: none !important;
  }

  // テーブルのカラムヘッダをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-columnHeader:focus-within,
  & .MuiDataGrid-columnHeader:focus {
    outline: none !important;
  }

  // テーブルヘッダーの背景色を設定
  .MuiDataGrid-columnHeaders {
    line-height: 1.5em !important;
  }

  // テーブルタイトルを太字に
  .MuiDataGrid-columnHeaderTitle {
    font-weight: 700;
  }
`;

export const makeGridColDef = (showSendToSamboKun: boolean): GridColDef[] => {
  const baseColumns: GridColDef[] = [
    {
      field: "id",
      headerName: "No",
      width: 90,
      sortable: false,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "realEstateType",
      headerName: "不動産種別",
      width: 220,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "location",
      headerName: "所在",
      width: 150,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "chibanKaokuNumber",
      headerName: "地番または家屋番号",
      width: 220,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "ownerAnalyzedAt",
      type: "dateTime",
      headerName: "登記取得日",
      width: 220,
      sortable: false,
      align: "center",
      headerAlign: "center",
      valueFormatter: (params: GridValueFormatterParams<string>) => {
        const value = params.value;
        if (value == null) return "";
        return format(new Date(value), "yyyy/MM/dd");
      },
    },
    {
      field: "previousOwnerAnalyzedAt",
      type: "dateTime",
      headerName: "前回取得日",
      width: 220,
      sortable: false,
      align: "center",
      headerAlign: "center",
      valueFormatter: (params: GridValueFormatterParams<string>) => {
        const value = params.value;
        if (value == null) return "";
        return format(new Date(value), "yyyy/MM/dd");
      },
    },
    {
      field: "uploadedAt",
      type: "dateTime",
      headerName: "アップロード日時",
      width: 220,
      sortable: false,
      align: "center",
      headerAlign: "center",
      valueFormatter: (params: GridValueFormatterParams<string>) => {
        const value = params.value;
        if (value == null) return "";
        return format(new Date(value), "yyyy/MM/dd HH:mm");
      },
    },
  ];

  return showSendToSamboKun
    ? [...baseColumns, createSamboKunColumn()]
    : baseColumns;
};

const CustomToolbar: React.FC<IProps> = (props) => {
  const { apiClient } = useApiClient();
  const isReadOnly = useReadOnlyUser();
  const disableOutput = props.disabledAction || props.requestId == null;
  const requestId = props.requestId ?? 0;
  const showSendToSamboKun = useShowSendToSamboKun();
  const { maxPropertiesPerRequest, isError: isLimitError } = useSamboKunLimit({
    enabled: showSendToSamboKun,
  });
  const [samboKunDialogOpen, setSamboKunDialogOpen] = useState<boolean>(false);

  // 連携ボタンの Disable 理由（undefined は "unknown-count" としてそのまま渡す）。
  // ローディング/エラー/物件数不明/上限超過を区別してツールチップ文言を切り替える。
  const disableReason = getSamboKunSendDisableReason(
    props.succeedCount,
    maxPropertiesPerRequest,
    isLimitError
  );
  const isLinkDisabled = disableReason !== "ok";
  // ボタン無効時のツールチップ文言。`hasTriggerPictureRequest=false` の固有
  // メッセージを最優先し、それ以外は連携不可理由のマップを引く。limit-loading は
  // 空文字にマップされ、Tooltip は disableHoverListener 側で抑止する。
  const samboKunTooltipText =
    props.hasTriggerPictureRequest === false
      ? "不動産登記取得結果に紐づく解析のみ連携できます"
      : disableReason !== "ok"
      ? samboKunSendDisabledTooltip[disableReason]
      : "";

  // LaunchDarkly フラグや SSO 状態変化で showSendToSamboKun が false 化したとき、
  // ダイアログ表示状態を閉じておく。閉じておかないと、再度 true になった際に
  // ユーザー操作なしでダイアログが復活してしまう。
  useEffect(() => {
    if (!showSendToSamboKun) {
      setSamboKunDialogOpen(false);
    }
  }, [showSendToSamboKun]);

  return (
    <>
      <GridToolbarContainer
        sx={{
          // https://mui.com/material-ui/react-button/#sizes と同様のpaddingを設定
          padding: "6px 16px",
        }}
      >
        <Stack direction={"row"} spacing={1}>
          <MuiButton
            variant={"outlined"}
            disabled={disableOutput}
            onClick={() => {
              if (props.requestId == null) return;
              void getOwnerAnalyzeFileApi(apiClient, {
                requestId: props.requestId,
                resType: "excel_design",
                fallBackName: `【色付き】所有者事項解析結果_${props.requestId}.xlsx`,
              }).catch(() => {
                toast.error("ファイルのダウンロードに失敗しました");
              });
            }}
          >
            エクセル出力（色付き）
            <DownloadIcon />
          </MuiButton>
          <OutputMenu
            disabled={disableOutput}
            requestId={requestId}
            taskId={props.taskId}
            identificationName={props.identificationName}
            showDesignExcel={false}
            isReadOnly={isReadOnly}
            isPropertyDetailOptionCredit={props.isPropertyDetailOptionCredit}
            isDmCredit={props.isDmCredit}
            onDownloadComplete={props.onDownloadComplete}
          />
          {showSendToSamboKun && (
            <Tooltip
              title={samboKunTooltipText}
              placement="top"
              arrow
              // limit-loading は空文字へマップされるため、空タイトル時はホバーを
              // 無効化して空の Tooltip 描画を抑止する
              disableHoverListener={
                (props.hasTriggerPictureRequest !== false && !isLinkDisabled) ||
                samboKunTooltipText === ""
              }
            >
              <span>
                <MuiButton
                  variant="outlined"
                  disabled={
                    disableOutput ||
                    props.hasTriggerPictureRequest === false ||
                    isLinkDisabled
                  }
                  onClick={() => {
                    setSamboKunDialogOpen(true);
                  }}
                >
                  参謀くんへ連携
                </MuiButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      </GridToolbarContainer>
      {showSendToSamboKun && props.requestId != null && (
        <SendToSamboKunDialog
          open={samboKunDialogOpen}
          onClose={() => {
            setSamboKunDialogOpen(false);
          }}
          target="owner"
          analyzeRequestId={props.requestId}
        />
      )}
    </>
  );
};

export const FeedGrid: React.FC<IProps> = (props) => {
  // LaunchDarkly フラグ + ローカルストレージの SSO 連携情報（ssoPartnerIds に
  // 参謀くんパートナー ID が含まれるか）の両方を満たすときのみ参謀くん連携 UI を表示。
  const showSendToSamboKun = useShowSendToSamboKun();
  const colDef = React.useMemo(
    () => makeGridColDef(showSendToSamboKun),
    [showSendToSamboKun]
  );
  const rows = React.useMemo(() => props.row ?? [], [props.row]);

  return (
    <StyledDataGrid
      components={{
        Toolbar: CustomToolbar,
      }}
      componentsProps={{
        toolbar: {
          requestId: props.requestId,
          disabledAction: props.disabledAction,
          taskId: props.taskId,
          identificationName: props.identificationName,
          isPropertyDetailOptionCredit: props.isPropertyDetailOptionCredit,
          isDmCredit: props.isDmCredit,
          hasTriggerPictureRequest: props.hasTriggerPictureRequest,
          succeedCount: props.succeedCount,
          onDownloadComplete: props.onDownloadComplete,
        },
      }}
      stickyHeader={true}
      autoHeight
      disableColumnMenu
      disableSelectionOnClick={true}
      rows={rows}
      columns={colDef}
      sortModel={[
        {
          field: "requestedAt",
          sort: "desc",
        },
      ]}
    />
  );
};
